import { Outlet, createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AppHeader } from '@/components/layout/app-header';
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';
import {
  SESSION_QUERY_KEY,
  isUnauthenticated,
  redirectToSignIn,
  useSession,
} from '@/features/auth/session';
import { useQueryClient } from '@tanstack/react-query';
import { scheduleSessionExpiry } from '@/lib/session-expiry';
import { homeVisibility } from '@/features/home/home-model';
import { ProductProvider, useProduct } from '@/features/home/product-context';
import { useProductBackground } from '@/features/home/use-product-background';
import { useBackButton } from '@/lib/device/back-button';
import { isOfflineGate } from '@/features/auth/offline-session';
import {
  OfflineBlockedScreen,
  PinSetupScreen,
  PinUnlockScreen,
} from '@/features/offline/pin-screens';
import { useOfflineState, usePinSetupNeeded } from '@/features/offline/use-offline';
import { isOfflineSession } from '@/lib/offline/grant';
import { onReconnect, startOfflineRuntime } from '@/lib/offline/runtime';

function AppErrorBoundary({ error }: { error: unknown }) {
  const router = useRouter();
  return (
    <RouteErrorBoundary
      error={error}
      boundary="app-shell"
      onRetry={() => void router.invalidate()}
    />
  );
}

/** Tints the whole shell for the selected product; must sit inside ProductProvider. */
function ProductBackground(): null {
  useProductBackground(useProduct().product);
  return null;
}

function AppLayout(): React.JSX.Element | null {
  const session = useSession();
  const queryClient = useQueryClient();

  // unified-access.md section 3.1 (SH-28): once the time left that the server
  // reported has passed and no save is in flight, ask the server again. Only
  // its 401 ends the session (the effect below then returns to Unified Access
  // with this page); a still-valid answer re-arms the timer. The device clock
  // is never compared with the server's expiry time.
  const { refetch, dataUpdatedAt } = session;
  const expiresInMs = session.data?.expiresInMs;
  useEffect(
    () =>
      scheduleSessionExpiry(
        expiresInMs,
        dataUpdatedAt,
        () => void refetch(),
        () => queryClient.isMutating() > 0,
      ),
    [expiresInMs, dataUpdatedAt, refetch, queryClient],
  );
  useBackButton();

  // fuel-pda.md 13.7: the sync loop, and reading the session online again as
  // soon as the server answers (an offline session then becomes a real one).
  useEffect(() => startOfflineRuntime(), []);
  useEffect(
    () => onReconnect(() => void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })),
    [queryClient],
  );

  const offline = useOfflineState();
  const onlineUserId =
    session.data && !isOfflineSession(session.data) ? session.data.user.id : null;
  const pinSetup = usePinSetupNeeded(onlineUserId, offline.available && offline.syncSupported);
  const [pinSetupDone, setPinSetupDone] = useState(false);

  useEffect(() => {
    // unified-access.md section 3, step 5: no session sends the browser to
    // Unified Access with a return address.
    if (!session.isPending && isUnauthenticated(session.error)) {
      redirectToSignIn();
    }
  }, [session.isPending, session.error]);

  if (session.isPending) return null;
  if (isUnauthenticated(session.error)) return null;
  if (isOfflineGate(session.error)) {
    // fuel-pda.md 13.4: the PIN first, or why the app cannot work offline.
    return session.error.reason === 'LOCKED' && session.error.userId ? (
      <PinUnlockScreen userId={session.error.userId} />
    ) : (
      <OfflineBlockedScreen reason={session.error.reason === 'EXPIRED' ? 'EXPIRED' : 'NO_GRANT'} />
    );
  }
  if (session.error) throw session.error;

  return (
    <ProductProvider allowed={homeVisibility(session.data.services).products}>
      <ProductBackground />
      <div className="mx-auto flex min-h-svh w-full max-w-[480px] flex-col bg-background">
        <AppHeader session={session.data} />
        <main className="flex flex-1 flex-col gap-4 p-4">
          {pinSetup.data === true && !pinSetupDone && onlineUserId ? (
            <PinSetupScreen userId={onlineUserId} onDone={() => setPinSetupDone(true)} />
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </ProductProvider>
  );
}

/** Everything under this layout requires a session. */
export const Route = createFileRoute('/_app')({
  component: AppLayout,
  errorComponent: AppErrorBoundary,
});
